import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet';
import { pairsApi, programsApi } from 'api';
import SubPageTitle from 'components/_common/panel/SubPageTitle';
import { dateFormat } from 'utils/dateFormat';
import { useProgramPanel } from 'contexts/programPanelContext';
import { Box, BoxSection } from 'components/_new/Box';
import { Avatar } from 'components/_new/Avatar';
import { Spinner } from 'components/_new/Spinner';
import { classNames } from 'primereact/utils';
import { parseHtml } from 'utils/htmlParser';
import { prepareUserAreas } from 'utils/prepareUserAreas';
import OptionItem from 'components/userPanel/pairing/OptionItem';
import { getPairStatusInfo } from 'components/programPanel/utils';
import passedIcon from 'assets/icons/new/passed.svg';
import failedIcon from 'assets/icons/new/failed.svg';
import { userDisplayName } from '../../utils/userDisplayName';
import { Button } from '../_new/Button';
import { DialogAction } from '../_new/Dialog';
import { Dropdown } from '../_common/forms/Dropdown';

export const CoordinatorPairDetails = () => {
	const { t } = useTranslation();

	const { data: program, isLoading, isSuccess } = useProgramPanel();

	const params = useParams<{ pairId: string }>();
	const pairId = Number(params.pairId);
	const [pairStatusModalOpen, setPairStatusModalOpen] = useState<boolean>(false);
	const [selectedValue, setSelectedValue] = useState<string>();

	const {
		data: programPair,
		isSuccess: getProgramPairSuccess,
		isLoading: getProgramPairLoading,
		refetch: refetchProgramPair,
	} = useQuery(
		['COORDINATOR_PAIR_DETAILS', { programId: program?.id, pairId }],
		() => programsApi.getPairAsCoordinator({ programId: Number(program?.id), pairId }),
		{
			enabled: Boolean(isSuccess && pairId),
		}
	);

	const { mutate: changePairStatus, isLoading: isChangingPairStatus } = useMutation(pairsApi.adminChangePairStatus, {
		onSuccess: (data) => {
			refetchProgramPair();
			setSelectedValue(undefined);
			setPairStatusModalOpen(false);
		},
	});

	const statusPairs = [
		{ label: t('programPanel.pair.list.status.accepted'), value: 'accepted', id: 1 },
		{ label: t('programPanel.pair.list.status.rejected'), value: 'rejected', id: 2 },
		{ label: t('programPanel.pair.list.status.finished'), value: 'finished', id: 3 },
		{ label: t('programPanel.pair.list.status.removed'), value: 'removed', id: 4 },
	];

	const handleSubmitForm = (event: { preventDefault: () => void }) => {
		event.preventDefault();
		if (selectedValue && program) {
			const newStatus = {
				programId: program.id,
				pairId,
				payload: selectedValue,
			};
			changePairStatus(newStatus);
		}
	};

	const getOptions = () => {
		const menteeAreas = programPair?.menteeApplication?.systemAnswers?.areas
			? prepareUserAreas(programPair.menteeApplication.systemAnswers.areas)
			: [];

		const mentorAreas = programPair?.mentorApplication?.systemAnswers?.areas
			? prepareUserAreas(programPair.mentorApplication.systemAnswers.areas)
			: [];

		const separatedAreas: any[] = [];

		menteeAreas?.forEach((area) => {
			const isMentorMatch = mentorAreas?.find((mentorArea) => mentorArea === area);
			if (isMentorMatch) {
				return separatedAreas.unshift({ area, match: true });
			}
			return separatedAreas.push({ area, match: false });
		});
		return separatedAreas;
	};

	const menteeData = useMemo(() => {
		if (programPair?.menteeApplication?.systemAnswers) {
			const systemAnswers = programPair?.menteeApplication?.systemAnswers;
			const { position, expectations, areas, department } = systemAnswers;
			return {
				applicationId: programPair.menteeApplicationId,
				avatar: programPair.menteeMembership.user.avatarActive
					? programPair.menteeMembership.user.avatar
					: undefined,
				name: userDisplayName(programPair.menteeMembership.user),
				avatarName: userDisplayName(programPair.menteeMembership.user, null),
				email: programPair.menteeMembership.user.email,
				position,
				expectations,
				areas,
				department,
				lastLoginDate: programPair.menteeMembership.user.lastLoginDate
					? dateFormat(programPair.menteeMembership.user.lastLoginDate, 'full')
					: '-',
			};
		}
		return null;
	}, [programPair]);

	const mentorData = useMemo(() => {
		if (programPair?.mentorApplication?.systemAnswers) {
			const systemAnswers = programPair?.mentorApplication?.systemAnswers;
			const { position, expectations, areas, department } = systemAnswers;
			return {
				applicationId: programPair.mentorApplicationId,
				avatar: programPair.mentorMembership.user.avatarActive
					? programPair.mentorMembership.user.avatar
					: undefined,
				name: userDisplayName(programPair.mentorMembership.user),
				avatarName: userDisplayName(programPair.mentorMembership.user, null),
				email: programPair.mentorMembership.user.email,
				position,
				expectations,
				areas,
				department,
				lastLoginDate: programPair.mentorMembership.user.lastLoginDate
					? dateFormat(programPair.mentorMembership.user.lastLoginDate, 'full')
					: '-',
			};
		}
		return null;
	}, [programPair]);

	const renderContent = (content: any, separator = ', ') => {
		if (content && Array.isArray(content)) {
			return content.join(separator);
		}
		if (content && String(content).length > 0) {
			return parseHtml(content);
		}
		return null;
	};

	const questionnairesData = useMemo(() => {
		const midGoals = Object.values(programPair?.questionnaires?.middle?.goals || {});
		const lastGoals = Object.values(programPair?.questionnaires?.last?.goals || {});

		const mappedGoals = (programPair?.goals || []).map((goal, index) => {
			const baseGoal = {
				key: `goal-${index}`,
				name: t('programPanel.goalRealizationNo', { no: index + 1 }),
				mid: `${midGoals[index] ? `${midGoals[index]}/10` : t('misc.noData')}`,
				last: `${lastGoals[index] ? `${lastGoals[index]}/10` : t('misc.noData')}`,
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			};

			if (program?.adminShowGoals) {
				return {
					...baseGoal,
					goalContent: goal.goal,
					measurableOutcome: goal.change,
					showDetails: true,
				};
			}

			return baseGoal;
		});

		const mappedQuestionnaires = [
			{
				key: 'menteeEngagement',
				name: t('programPanel.questionaireHeaders.menteeEngagement'),
				mid: programPair?.questionnaires?.middle?.menteeEngagement
					? `${programPair?.questionnaires?.middle?.menteeEngagement}/10`
					: t('misc.noData'),
				last: programPair?.questionnaires?.last?.menteeEngagement
					? `${programPair?.questionnaires?.last?.menteeEngagement}/10`
					: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'mentorEngagement',
				name: t('programPanel.questionaireHeaders.mentorEngagement'),
				mid: programPair?.questionnaires?.middle?.mentorEngagement
					? `${programPair?.questionnaires?.middle?.mentorEngagement}/10`
					: t('misc.noData'),
				last: programPair?.questionnaires?.last?.mentorEngagement
					? `${programPair?.questionnaires?.last?.mentorEngagement}/10`
					: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'menteeSatisfaction',
				name: t('programPanel.questionaireHeaders.menteeSatisfaction'),
				mid: programPair?.questionnaires?.middle?.menteeSatisfaction
					? `${programPair?.questionnaires?.middle?.menteeSatisfaction}/10`
					: t('misc.noData'),
				last: programPair?.questionnaires?.last?.menteeSatisfaction
					? `${programPair?.questionnaires?.last?.menteeSatisfaction}/10`
					: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'processStandardsMaintained',
				name: t('programPanel.questionaireHeaders.processStandardsMaintained'),
				mid:
					// eslint-disable-next-line no-nested-ternary
					programPair?.questionnaires?.middle?.standards !== undefined
						? programPair?.questionnaires?.middle?.standards
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				last:
					// eslint-disable-next-line no-nested-ternary
					programPair?.questionnaires?.last?.standards !== undefined
						? programPair?.questionnaires?.last?.standards
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'meetingSchedule',
				name: t('programPanel.questionaireHeaders.meetingSchedule'),
				mid:
					// eslint-disable-next-line no-nested-ternary
					programPair?.questionnaires?.middle?.schedule !== undefined
						? programPair?.questionnaires?.middle?.schedule
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				last:
					// eslint-disable-next-line no-nested-ternary
					programPair?.questionnaires?.last?.schedule !== undefined
						? programPair?.questionnaires?.last?.schedule
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
			{
				key: 'contractRulesFollowed',
				name: t('programPanel.questionaireHeaders.contractRulesFollowed'),
				mid:
					// eslint-disable-next-line no-nested-ternary
					programPair?.questionnaires?.middle?.contract !== undefined
						? programPair?.questionnaires?.middle?.contract
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				last:
					// eslint-disable-next-line no-nested-ternary
					programPair?.questionnaires?.last?.contract !== undefined
						? programPair?.questionnaires?.last?.contract
							? t('misc.yes')
							: t('misc.no')
						: t('misc.noData'),
				showDetails: false,
				goalContent: '',
				measurableOutcome: '',
			},
		];

		return [...mappedGoals, ...mappedQuestionnaires];
	}, [programPair, program?.adminShowGoals, t]);

	const renderGoalsInDevelopmentPlan = () => {
		if (!program?.adminShowGoals || !programPair?.goals || programPair.goals.length === 0) {
			return null;
		}

		return (
			<div className="mt-4 mb-4">
				<div className="flex flex-column gap-3">
					{programPair.goals.map((goal, index) => (
						<div key={`goal-${goal.id}`} className="p-3 border-round bg-gray-50 flex ">
							<div className="w-3 align-self-center">{`${t('userPanel.goal.goal')} #${index + 1}`}:</div>
							<div>
								<div className="font-bold">{goal.goal}</div>
								<div className="text-sm color-gray mt-1">{goal.change}</div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderQuestionnaireTable = () => {
		return (
			<table className="p-datatable w-full">
				<thead className="p-datatable-thead">
					<tr>
						<th>{t('organizationPanel.questionBody')}</th>
						<th>{t('organizationPanel.questionnaireMid')}</th>
						<th>{t('organizationPanel.questionnaireLast')}</th>
					</tr>
				</thead>
				<tbody className="p-datatable-tbody">
					{questionnairesData.map((row) => (
						<React.Fragment key={row.key}>
							<tr>
								<td>
									{row.name}
									{row.showDetails && (
										<>
											<div className="text-sm mt-1 font-bold">{row.goalContent}</div>
											<div className="text-sm color-gray mt-1">{row.measurableOutcome}</div>
										</>
									)}
								</td>
								<td>{row.mid}</td>
								<td>{row.last}</td>
							</tr>
						</React.Fragment>
					))}
				</tbody>
			</table>
		);
	};

	return (
		<>
			<Helmet title={t('organizationPanel.programPairs')} />

			<SubPageTitle
				title={t('organizationPanel.programPairs')}
				additional={program?.displayName}
				right={() => (
					<Link to={`/coordinator/${program?.name}/pair/list`} className="text-purplish-blue">
						{t('programPanel.pair.backToPairList')}
					</Link>
				)}
			/>
			<DialogAction
				title={t('userPanel.mentibotAi.reply.ratingComment.title')}
				visible={pairStatusModalOpen}
				onHide={() => {
					setPairStatusModalOpen(false);
				}}
				actions={[
					{
						key: 'save',
						form: 'pair-status-form',
						label: t('actions.save'),
						loading: isChangingPairStatus,
						submit: true,
						disabled: !selectedValue,
					},
				]}
			>
				<form
					id="pair-status-form"
					className="w-full flex flex-column justify-content-between"
					onSubmit={handleSubmitForm}
				>
					<p>{t('programPanel.pair.list.chooseNewPairStatus')}</p>
					<Dropdown
						placeholder={t('programPanel.pair.list.changePairStatus')}
						className="flex-1"
						value={selectedValue}
						onChange={(event) => {
							setSelectedValue(event.value);
						}}
						options={statusPairs}
						rounded
					/>
				</form>
			</DialogAction>

			{(isLoading || getProgramPairLoading) && <Spinner />}

			{program && isSuccess && getProgramPairSuccess && programPair && menteeData && mentorData && (
				<div className="flex flex-column gap-4">
					<Box variant="white-bordered">
						<BoxSection>
							<div className="flex flex-row flex-wrap gap-2">
								<div className="flex-1">
									<div className="color-gray mb-2">{t('misc.mentee')}:</div>
									<div className="flex flex-row justify-content-start gap-4 w-full">
										<Avatar src={menteeData?.avatar} name={menteeData.avatarName} size="lg" />
										<div>
											<Link
												to={`/coordinator/${program.name}/application/${menteeData.applicationId}`}
												className={classNames('font-bold text-3xl strong text-wrap-any')}
											>
												{menteeData.name}
											</Link>
											{menteeData.position && (
												<div className="text-sm strong">{menteeData.position}</div>
											)}
											<div className="text-sm strong">{menteeData.email}</div>
											<div>
												{t('programPanel.lastLogin')}: {menteeData.lastLoginDate}
											</div>
										</div>
									</div>
								</div>
								<div className="flex-1">
									<div className="color-gray mb-2">{t('misc.mentor')}:</div>
									<div className="flex flex-row justify-content-start gap-4 w-full">
										<Avatar src={mentorData.avatar} name={mentorData.avatarName} size="lg" />
										<div>
											<Link
												to={`/coordinator/${program.name}/application/${mentorData.applicationId}`}
												className="font-bold text-3xl strong text-wrap-any"
											>
												{mentorData.name}
											</Link>
											{mentorData.position && (
												<div className="text-sm strong">{mentorData.position}</div>
											)}
											<div className="text-sm strong">{mentorData.email}</div>
											<div>
												{t('programPanel.lastLogin')}: {mentorData.lastLoginDate}
											</div>
										</div>
									</div>
								</div>
							</div>
							<hr />
							<div className="mt-4">
								<div className="color-gray">{t('programPanel.pair.menteeGoals')}:</div>
								<div className="flex mt-1 gap-2 flex-wrap flex-start sm:flex-column md:flex-row">
									{getOptions().map((area) => (
										<OptionItem key={area.area} option={area} />
									))}
								</div>
							</div>
							<div className="my-4">
								<div className="color-gray">{t('programPanel.pair.menteeExpectations')}:</div>
								<div className="flex mt-1 gap-2 flex-wrap flex-start">
									{renderContent(menteeData.expectations)}
								</div>
							</div>
							<hr />
							<div className="flex justify-content-between align-items-center">
								<div>{getPairStatusInfo(programPair)}, </div>
								{programPair?.contractId && (
									<div>
										<Button
											onClick={() => setPairStatusModalOpen(true)}
											label={t('programPanel.pair.list.changePairStatus')}
										/>
									</div>
								)}
							</div>
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('userPanel.navigation.contract')}>
							<div className="flex flex-column gap-4">
								<div className="flex flex-row">
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.menteeSign')}:
												</span>
												{programPair?.contract?.initialSignDateMentee ? (
													<span className="flex align-items-center gap-2">
														<img
															src={passedIcon}
															alt="passed"
															style={{ width: 22, height: 22 }}
														/>
														<span>
															{dateFormat(
																programPair?.contract?.initialSignDateMentee,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img
															src={failedIcon}
															alt="failed"
															style={{ width: 22, height: 22 }}
														/>
														<span>-</span>
													</span>
												)}
											</div>
										</div>
									</div>
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.mentorSign')}:
												</span>
												{programPair?.contract?.initialSignDateMentor ? (
													<span className="flex align-items-center gap-2">
														<img
															src={passedIcon}
															alt="passed"
															style={{ width: 22, height: 22 }}
														/>
														<span>
															{dateFormat(
																programPair?.contract?.initialSignDateMentor,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img
															src={failedIcon}
															alt="failed"
															style={{ width: 22, height: 22 }}
														/>
														<span>-</span>
													</span>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('userPanel.navigation.mentoringPlan')}>
							<div className="flex flex-column gap-4">
								<div className="flex flex-row">
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.menteeSign')}:
												</span>
												{programPair?.contract?.goalSignDateMentee ? (
													<span className="flex align-items-center gap-2">
														<img
															src={passedIcon}
															alt="passed"
															style={{ width: 22, height: 22 }}
														/>
														<span>
															{dateFormat(
																programPair?.contract?.goalSignDateMentee,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img
															src={failedIcon}
															alt="failed"
															style={{ width: 22, height: 22 }}
														/>
														<span>-</span>
													</span>
												)}
											</div>
										</div>
									</div>
									<div className="col-6">
										<div className="flex flex-column">
											<div className="flex align-items-center gap-2 mb-2">
												<span className="font-medium">
													{t('programPanel.pair.mentorSign')}:
												</span>
												{programPair?.contract?.goalSignDateMentor ? (
													<span className="flex align-items-center gap-2">
														<img
															src={passedIcon}
															alt="passed"
															style={{ width: 22, height: 22 }}
														/>
														<span>
															{dateFormat(
																programPair?.contract?.goalSignDateMentor,
																'full'
															)}
														</span>
													</span>
												) : (
													<span className="flex align-items-center gap-2">
														<img
															src={failedIcon}
															alt="failed"
															style={{ width: 22, height: 22 }}
														/>
														<span>-</span>
													</span>
												)}
											</div>
										</div>
									</div>
								</div>
							</div>
							{renderGoalsInDevelopmentPlan()}
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('userPanel.progressSteps.steps.after_mid_session')}>
							<table className="p-datatable w-full">
								<thead className="p-datatable-thead">
									<tr>
										<th>{t('programPanel.number')}</th>
										<th>{t('programPanel.date')}</th>
										<th>{t('programPanel.pair.sessionDuration')}</th>
										<th>{t('programPanel.pair.menteeSessionRating')}</th>
										<th>{t('programPanel.pair.mentorSessionRating')}</th>
									</tr>
								</thead>
								<tbody className="p-datatable-tbody">
									{programPair.sessions?.map((session) => (
										<tr key={session.id}>
											<td>
												<div className="flex flex-row gap-2">
													<span>{session.sessionNumber}</span>
													{session.sessionType === 'middle' && (
														<span>{t('misc.middleSession')}</span>
													)}
												</div>
											</td>
											<td>
												{session.date ? (
													<div className="flex flex-row gap-2">
														<span>{dateFormat(session.date, 'full')}</span>
														{session.finished && (
															<i className="pi pi-check-circle text-green-400" />
														)}
													</div>
												) : (
													'-'
												)}
											</td>
											<td>
												{((): any => {
													if (session.duration) {
														return `${session.duration} min`;
													}
													if (programPair.contract?.sessionTime) {
														return `${t('programPanel.pair.accordingToContract')} (${
															programPair.contract.sessionTime
														} min)`;
													}
													return '-';
												})()}
											</td>
											<td>
												{session?.menteeRating ? (
													<div className="flex flex-column gap-1">
														<div>{`${session.menteeRating}/5`}</div>
														{session?.menteeRatingComment && (
															<div className="text-sm text-500">
																{session.menteeRatingComment}
															</div>
														)}
													</div>
												) : (
													'-'
												)}
											</td>
											<td>
												{session?.mentorRating ? (
													<div className="flex flex-column gap-1">
														<div>{`${session.mentorRating}/5`}</div>
														{session?.mentorRatingComment && (
															<div className="text-sm text-500">
																{session.mentorRatingComment}
															</div>
														)}
													</div>
												) : (
													'-'
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</BoxSection>
					</Box>

					<Box variant="white-bordered">
						<BoxSection header={t('organizationPanel.questionnaireHeader')}>
							{renderQuestionnaireTable()}
						</BoxSection>
					</Box>
				</div>
			)}
		</>
	);
};
